import type { FlowGraph } from './graph';
import { indexGraph } from './resolve';
import type { Issue } from './validate';

/** What the check needs to know about each of the workspace's personas. */
export interface PersonaFacts {
  id: string;
  name: string;
  accountCount: number;
  referenceCount: number;
}

const PUBLISH = 'output.schedulePublish';
const VARIATIONS = 'generate.personaVariations';
const SLIDES = 'generate.carouselSlides';

const ids = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

/** "Bea", "Bea and Zoe", "Bea, Zoe and Ana". */
const names = (list: string[]) =>
  list.length <= 1
    ? (list[0] ?? '')
    : `${list.slice(0, -1).join(', ')} and ${list.at(-1)}`;

/**
 * Persona posts that Schedule & publish (posting to persona accounts) would
 * have to hold: posts made for a persona it doesn't post for, or for one with
 * no accounts. Needs the workspace's personas, so it sits beside
 * `validateGraph` rather than in it. Only personas a node sets up front are
 * known here (Persona variations, Carousel slides' "Show a persona"); ones
 * tagged on library images only show at run time.
 */
export function personaIssues(graph: FlowGraph, personas: PersonaFacts[]): Issue[] {
  const index = indexGraph(graph);
  const byId = new Map(personas.map((p) => [p.id, p]));
  const issues: Issue[] = [];

  /** Every node upstream of `id` (not `id` itself). */
  const upstream = (id: string) => {
    const seen = new Set<string>();
    const queue = [id];
    while (queue.length) {
      const next = queue.pop()!;
      for (const edge of index.incoming.get(next) ?? []) {
        if (seen.has(edge.source.nodeId)) continue;
        seen.add(edge.source.nodeId);
        queue.push(edge.source.nodeId);
      }
    }
    return [...seen].flatMap((nodeId) => {
      const entry = index.nodes.get(nodeId);
      return entry ? [entry] : [];
    });
  };

  for (const { node } of index.nodes.values()) {
    if (node.type !== PUBLISH || node.config.postTo !== 'personaAccounts') continue;

    // The personas whose posts reach this node, and who decides them.
    const made = new Map<string, string>();
    for (const { node: from, definition } of upstream(node.id)) {
      const label = from.label ?? definition?.label ?? from.type;
      if (from.type === VARIATIONS) {
        const picked = ids(from.config.personaIds);
        const list = picked.length
          ? picked
          : personas.filter((p) => p.referenceCount > 0).map((p) => p.id);
        for (const id of list) if (!made.has(id)) made.set(id, label);
      } else if (from.type === SLIDES && typeof from.config.personaId === 'string') {
        if (!made.has(from.config.personaId)) made.set(from.config.personaId, label);
      }
    }

    const ticked = ids(node.config.personaIds);
    const postsFor = new Set(
      (ticked.length ? ticked : personas.map((p) => p.id)).filter(
        (id) => (byId.get(id)?.accountCount ?? 0) > 0,
      ),
    );
    const warn = (code: Issue['code'], message: string) =>
      issues.push({ code, severity: 'warning', message, nodeId: node.id });

    if (postsFor.size === 0) {
      warn(
        'NO_PERSONA_ACCOUNTS',
        ticked.length
          ? 'None of the personas ticked here has an account, so every post will be held. Add one in Personas.'
          : 'No persona has an account yet, so every post will be held. Add one in Personas.',
      );
      continue;
    }

    const known = [...made.keys()].filter((id) => byId.has(id));
    const noAccounts = known.filter((id) => byId.get(id)!.accountCount === 0);
    const notTicked = known.filter(
      (id) => byId.get(id)!.accountCount > 0 && !postsFor.has(id),
    );
    const nameOf = (id: string) => byId.get(id)!.name;

    if (notTicked.length) {
      const sources = [...new Set(notTicked.map((id) => made.get(id)!))];
      warn(
        'PERSONA_NOT_TICKED',
        `${names(sources)} ${sources.length > 1 ? 'make' : 'makes'} posts for ${names(
          notTicked.map(nameOf),
        )}, who ${notTicked.length > 1 ? 'aren’t' : 'isn’t'} ticked here. Those posts will be held.`,
      );
    }
    if (noAccounts.length) {
      warn(
        'PERSONA_NO_ACCOUNTS',
        `${names(noAccounts.map(nameOf))} ${
          noAccounts.length > 1 ? 'have' : 'has'
        } no accounts yet, so their posts will be held. Add one in Personas.`,
      );
    }
  }
  return issues;
}
